import React, { useState, useEffect, useMemo } from "react";
import {
  useStripe,
  useElements,
  CardElement,
  Elements,
} from "@stripe/react-stripe-js";
import { toast } from "react-toastify";
import { captureError } from "../../utils/sentry";
import { useTranslation } from "react-i18next";
import {
  getStripePaymentMethods,
  updateCard,
  updatePaymentMethod,
  getAllPaymentMethods,
  createACHSetupIntent,
  setDefaultPaymentMethod,
  getPaymentByMember,
} from "../../api/paymentsAPI";
import { useDispatch, useSelector } from "react-redux";
import { selectMembershipDetails } from "../../store/slices/membershipSlice";
import AutopaySwitch from "../user/AutopaySwitch";
import { useTheme } from "../../context/ThemeContext";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import { getCardBackground, getPrimaryText, getSecondaryText, getLightBorder } from "../../utils/themeUtils";
import {
  getStripePromise,
  resetStripeClient,
  getStripeLoadError,
  isCardProcessingNotSetUp,
} from "../../utils/stripeClient";
import BankAccountElement from "../payment/stripe/BankAccountElement";
import Tooltip from "../common/Tooltip";
import { persistAuthToken } from "../../api/authTokenStore";
import { PageContainer, PageHeading } from "../ui";

import PaymentMethodSetupEmail from "./PaymentMethodSetupEmail";
import { defaultMethodIdFrom } from "../../utils/savedPaymentMethods";
import CardExpiryBadge from "../billing/CardExpiryBadge";
import { useHasLayeredPermission } from "../../hooks/useHasPermission";
// Workaround for TypeScript cache issue with new export
const { deletePaymentMethod } = require("../../api/paymentsAPI");

interface UpdateCardFormProps {
  source?: string;
  membership?: any; // Optional override for membership data (used when admin/staff views EditMembershipPage)
  /**
   * Show the staff "Send payment-method setup email" tool. Only the staff
   * membership page passes this; a member looking at their own Payment
   * Settings never sees it (and the backend would refuse them).
   */
  canSendSetupEmail?: boolean;
  /**
   * Set by the wrapper when Stripe.js resolved to nothing (no key for this
   * club, a timeout, or a blocker). Without it the banner read "Loading
   * secure payment form…" forever: nothing re-rendered when loading ended.
   */
  stripeFailed?: boolean;
  /**
   * Staff working on SOMEONE ELSE'S account (the staff membership page).
   * Then seeing the saved methods needs process_payments plus
   * view_member_payment_methods, and adding, removing or changing the default
   * needs process_payments plus manage_member_payment_methods (PM 9123a167;
   * each new one counts as held while it is not defined in the club, as on
   * the server). A family on its own Payment Settings never needs either.
   */
  staffActingForMember?: boolean;
}

const NO_VIEW_PERMISSION =
  "Your role does not include seeing this member's saved payment methods. A club owner can turn on \"Process Payments\" and \"View Member Payment Methods\" in Roles & Permissions.";
const NO_MANAGE_PERMISSION =
  "Your role does not include adding, removing, or changing this member's payment methods. A club owner can turn on \"Process Payments\" and \"Manage Member Payment Methods\" in Roles & Permissions.";

/** The server's answer when this club cannot take cards (backend utils/stripeUnavailable). */
const PAYMENTS_UNAVAILABLE = "PAYMENTS_UNAVAILABLE";
const METHODS_LOAD_FAILED = "Saved payment methods could not be loaded. Try again in a moment.";
const CARDS_NOT_SET_UP = "Card payments are not set up for this club, so saved cards cannot be shown or added here.";

const UpdateCardForm: React.FC<UpdateCardFormProps> = ({
  source,
  membership: propMembership,
  canSendSetupEmail = false,
  stripeFailed = false,
  staffActingForMember = false,
}) => {
  const { isDarkMode } = useTheme();
  const { confirm, confirmModal } = useConfirmModal();
  const { t } = useTranslation(['forms', 'common']);
  const staffMayView = useHasLayeredPermission("process_payments", "view_member_payment_methods");
  const staffMayManage = useHasLayeredPermission("process_payments", "manage_member_payment_methods");
  const canViewMethods = !staffActingForMember || staffMayView;
  const canManageMethods = !staffActingForMember || staffMayManage;
  // STATE
  const [savedCards, setSavedCards] = useState<any[]>([]);
  const [savedBankAccounts, setSavedBankAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [cardLoading, setCardLoading] = useState(false);
  const [bankAccountLoading, setBankAccountLoading] = useState(false);
  // A failed load is not "no saved cards" (2026-10-09: a 503 read as none).
  const [methodsLoadError, setMethodsLoadError] = useState<string | null>(null);
  const [paymentsNotSetUp, setPaymentsNotSetUp] = useState(false);
  const [achClientSecret, setAchClientSecret] = useState("");
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState<
    string | null
  >(null); // store default card id
  const [, setIsMobileDevice] = useState(false);
  const [, setIsMobileLandscape] = useState(false);
  const [stripeCustomerId, setStripeCustomerId] = useState<string>(""); // Fetch from membership
  const [deletingCardId, setDeletingCardId] = useState<string | null>(null); // Track which card is being deleted
  const [deletingBankAccountId, setDeletingBankAccountId] = useState<string | null>(null);
  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);
  const [showAddBankAccount, setShowAddBankAccount] = useState(false);
  // Stripe
  const stripe = useStripe();
  const elements = useElements();
  // HELPER
  const token = localStorage.getItem("authToken");
  const reduxMembership: any = useSelector(selectMembershipDetails);
  // Use prop membership if provided, otherwise fall back to Redux
  const membership = propMembership || reduxMembership;
  useDispatch();

  // handle form submit. SetupIntent is created on-demand here, not on mount —
  // the backend handles customer creation if needed and returns the
  // client_secret we use to confirm via Stripe.js.
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) {
      toast.error(t('forms:paymentSettings.toast.unableSetupPayment'));
      captureError(new Error("UpdateCardForm: stripe/elements null at submit"), {
        component: "UpdateCardForm",
        operation: "stripeNotReady",
      });
      return;
    }
    if (!membership?.id) {
      toast.error(t('forms:paymentSettings.toast.unableSetupPayment'));
      return;
    }

    setLoading(true);
    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      toast.error(t('forms:paymentSettings.toast.cardNotFound'));
      setLoading(false);
      return;
    }

    // 1. Ask backend for a fresh SetupIntent (also creates customer if needed)
    let setupResponse: any;
    try {
      setupResponse = await updateCard(token, {
        stripeCustomerId: membership.stripe_customer_id || null,
        membershipId: membership.id,
        email: membership.email,
        firstName: membership.first_name,
        lastName: membership.last_name,
      });
    } catch {
      toast.error(t('forms:paymentSettings.toast.failedInitPayment'));
      setLoading(false);
      return;
    }
    if (!setupResponse?.clientSecret) {
      toast.error(t('forms:paymentSettings.toast.failedInitCardSetup'));
      setLoading(false);
      return;
    }

    // If backend returned a different publishable key than we have cached,
    // sync localStorage and reset the Stripe client. Defensive; only fires
    // when the tenant rotated keys mid-session.
    if (setupResponse?.stripePublicKey) {
      const currentKey = localStorage.getItem("stripePublicKey");
      if (currentKey !== setupResponse.stripePublicKey) {
        localStorage.setItem("stripePublicKey", setupResponse.stripePublicKey);
        resetStripeClient();
        toast.warning(t('forms:paymentSettings.toast.paymentConfigUpdated'));
        setLoading(false);
        return; // Force the user to re-submit with the refreshed Stripe client
      }
    }

    const resolvedCustomerId = setupResponse?.stripeCustomerId || membership.stripe_customer_id;
    if (resolvedCustomerId) setStripeCustomerId(resolvedCustomerId);

    // 2. Confirm the SetupIntent with the new card details
    const { error, setupIntent } = await stripe.confirmCardSetup(setupResponse.clientSecret, {
      payment_method: {
        card: cardElement,
      },
    });

    if (error) {
      toast.error(t('forms:paymentSettings.toast.errorUpdatingCard') + " " + error.message);
      setLoading(false);
      return;
    }

    // 3. Tell the backend to associate the new payment method with the customer
    try {
      // updatePaymentMethod never throws: a refusal resolves to
      // { success: false, error }. The answer used to be discarded, so the
      // family was told "card updated" when the club's records were not.
      const saved = await updatePaymentMethod(token, {
        stripeCustomerId: resolvedCustomerId,
        paymentMethodId: setupIntent.payment_method,
      });

      if (!saved?.success) {
        toast.error(saved?.error || t('forms:paymentSettings.toast.failedUpdateCard'));
        await fetchSavedCards();
        return;
      }

      toast.success(t('forms:paymentSettings.toast.cardUpdatedSuccess'));
      await fetchSavedCards();
    } catch (error) {
      toast.error(t('forms:paymentSettings.toast.failedUpdateCard'));
    } finally {
      setLoading(false);
    }
  };

  // Handle delete payment method
  const handleDeleteCard = async (paymentMethodId: string, cardLast4: string) => {
    // Confirm deletion (styled modal — confirm-dialog sweep)
    const confirmed = await confirm({
      title: t('forms:paymentSettings.removeCard', { defaultValue: 'Remove card?' }),
      message: t('forms:paymentSettings.toast.confirmDeleteCard', { last4: cardLast4 }),
      confirmText: t('forms:paymentSettings.remove', { defaultValue: 'Remove' }),
    });

    if (!confirmed) return;

    setDeletingCardId(paymentMethodId);

    try {
      const response = await deletePaymentMethod(token, paymentMethodId);

      if (response?.success) {
        toast.success(t('forms:paymentSettings.toast.cardDeletedSuccess'));
        // Refresh saved cards list
        await fetchSavedCards();
      } else {
        toast.error(response?.message || t('forms:paymentSettings.toast.failedDeleteCard'));
      }
    } catch (error: any) {
      // Axios interceptor handles Sentry capture
      // The server's own words, e.g. "Automatic payments use this card. Set
      // another card as the default first, or turn automatic payments off."
      const serverMessage = error?.response?.data?.error || error?.response?.data?.message;
      toast.error(serverMessage || t('forms:paymentSettings.toast.errorDeletingCard') + " " + (error?.message || "Unknown error"));
    } finally {
      setDeletingCardId(null);
    }
  };

  // Handle delete bank account
  const handleDeleteBankAccount = async (paymentMethodId: string, last4: string) => {
    const confirmed = await confirm({
      title: t('forms:paymentSettings.removeBankAccount', { defaultValue: 'Remove bank account?' }),
      message: t('forms:paymentSettings.toast.confirmDeleteBankAccount', { last4 }),
      confirmText: t('forms:paymentSettings.remove', { defaultValue: 'Remove' }),
    });

    if (!confirmed) return;

    setDeletingBankAccountId(paymentMethodId);

    try {
      const response = await deletePaymentMethod(token, paymentMethodId);

      if (response?.success) {
        toast.success(t('forms:paymentSettings.toast.bankAccountDeletedSuccess'));
        await fetchSavedCards();
      } else {
        toast.error(response?.message || t('forms:paymentSettings.toast.failedDeleteBankAccount'));
      }
    } catch (error: any) {
      // Axios interceptor handles Sentry capture
      const serverMessage = error?.response?.data?.error || error?.response?.data?.message;
      toast.error(serverMessage || t('forms:paymentSettings.toast.errorDeletingBankAccount') + " " + (error?.message || "Unknown error"));
    } finally {
      setDeletingBankAccountId(null);
    }
  };

  // Handle setting default payment method
  const handleSetDefault = async (paymentMethodId: string) => {
    if (!stripeCustomerId) {
      toast.error(t('forms:paymentSettings.toast.customerInfoNotAvailable'));
      return;
    }

    setSettingDefaultId(paymentMethodId);

    try {
      const response = await setDefaultPaymentMethod(token, {
        customerId: stripeCustomerId,
        paymentMethodId,
      });

      if (response?.success) {
        toast.success(t('forms:paymentSettings.toast.defaultPaymentMethodUpdated'));
        setDefaultPaymentMethodId(paymentMethodId);
        await fetchSavedCards();
      } else {
        toast.error(response?.message || t('forms:paymentSettings.toast.failedSetDefault'));
      }
    } catch (error: any) {
      // Axios interceptor handles Sentry capture
      toast.error(t('forms:paymentSettings.toast.errorSettingDefault') + " " + (error?.message || "Unknown error"));
    } finally {
      setSettingDefaultId(null);
    }
  };

  // Create ACH Setup Intent for adding bank account
  const createAchSetupIntent = async () => {
    if (!stripeCustomerId) {
      toast.error(t('forms:paymentSettings.toast.customerInfoNotAvailable'));
      return;
    }

    try {
      const response = await createACHSetupIntent(token, {
        stripeCustomerId: stripeCustomerId,
        membershipId: membership?.id,
      });

      if (response?.success && response?.clientSecret) {
        setAchClientSecret(response.clientSecret);
        setShowAddBankAccount(true);
      } else {
        toast.error(response?.error || t('forms:paymentSettings.toast.failedInitBankAccount'));
      }
    } catch (error: any) {
      // Axios interceptor handles Sentry capture
      const errorMessage = error?.response?.data?.error || error?.message || t('forms:paymentSettings.toast.failedInitBankAccount');
      toast.error(errorMessage);
    }
  };

  // Handle bank account added successfully
  const handleBankAccountSuccess = async (paymentMethodId: string) => {
    setShowAddBankAccount(false);
    setAchClientSecret("");
    await fetchSavedCards();
  };


  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ) || window.innerWidth <= 768;

      const isLandscape = window.innerWidth > window.innerHeight && isMobile;

      setIsMobileDevice(isMobile);
      setIsMobileLandscape(isLandscape);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    window.addEventListener('orientationchange', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('orientationchange', checkMobile);
    };
  }, []);

  // Get Stripe customer ID from membership data or payment history
  useEffect(() => {
    const fetchStripeCustomerId = async () => {
      // First, try to get from membership record
      if (membership?.stripe_customer_id) {
        setStripeCustomerId(membership.stripe_customer_id);
        return;
      }

      // Fallback: try to get from payment history
      if (membership?.id) {
        try {
          const response = await getPaymentByMember(token, membership.id);
          if (response?.data?.data && response.data.data.length > 0) {
            const payments = response.data.data;
            // Find the first payment with a valid stripe_customer_id
            const stripePayment = payments.find(
              (payment: any) =>
                typeof payment.stripe_customer_id === "string" &&
                payment.stripe_customer_id.trim() !== "" &&
                payment.method !== "cash" &&
                payment.method !== "cheque"
            );

            if (stripePayment?.stripe_customer_id) {
              setStripeCustomerId(stripePayment.stripe_customer_id);
              return;
            }
          }
        } catch (error) {
          // Axios interceptor handles Sentry capture
        }
      }
    };

    fetchStripeCustomerId();
  }, [membership?.id, membership?.stripe_customer_id]);

  // Fetch saved payment methods (cards and bank accounts)
  const fetchSavedCards = async () => {
    if (!stripeCustomerId) {
      return;
    }
    setCardLoading(true);
    setBankAccountLoading(true);
    setMethodsLoadError(null);
    try {
      // Try to use getAllPaymentMethods first, fall back to getStripePaymentMethods
      const response = await getAllPaymentMethods(token, stripeCustomerId);

      // CLUB-CENTRAL-TENANT-3Q (2026-06-03): the fallback path used to
      // dump fallbackResponse?.data directly into savedCards, and if
      // that array contained any null/partial entries (Stripe returns
      // partial pages on rate-limit / network blips), the render at
      // L517 read `.id` on null and crashed. Filter nulls at every
      // setter — cheaper than peppering optional chains in 6 places.
      const onlyWithId = (arr: unknown): any[] =>
        Array.isArray(arr) ? arr.filter((x: any) => x && x.id) : [];

      if (response?.success) {
        // API returns cards in data or cards field, not paymentMethods
        const allMethods = onlyWithId(response?.data || response?.cards);

        // Separate cards and bank accounts
        const cards = allMethods.filter((pm: any) => pm.type === 'card');
        const bankAccounts = allMethods.filter((pm: any) => pm.type === 'us_bank_account');

        setSavedCards(cards);
        setSavedBankAccounts(bankAccounts);

        // Only what Stripe says is the default. Labeling the first card
        // "Default" when Stripe has none told families automatic payments
        // had a card to use when the charge would fail as "No default
        // payment method".
        setDefaultPaymentMethodId(response?.defaultPaymentMethodId || null);

        if (response?.token) {
          persistAuthToken(response.token);
        }
      } else {
        // Fallback to old API
        const fallbackResponse = await getStripePaymentMethods(token, stripeCustomerId);
        if (fallbackResponse?.success) {
          const safeCards = onlyWithId(fallbackResponse?.data);
          setSavedCards(safeCards);
          setSavedBankAccounts([]);
          setDefaultPaymentMethodId(
            defaultMethodIdFrom(fallbackResponse) || null
          );
          if (fallbackResponse?.token) {
            persistAuthToken(fallbackResponse.token);
          }
        } else {
          setMethodsLoadError(METHODS_LOAD_FAILED);
        }
      }
    } catch (error: any) {
      // Axios interceptor handles Sentry capture.
      // A club that cannot take cards answers 503 PAYMENTS_UNAVAILABLE: that
      // is an answer, not a fault, and retrying the older endpoint cannot
      // change it.
      const body = error?.response?.data;
      if (body?.errorCode === PAYMENTS_UNAVAILABLE) {
        setPaymentsNotSetUp(true);
        setMethodsLoadError(body?.error || CARDS_NOT_SET_UP);
      } else {
        // Try fallback
        try {
          const fallbackResponse = await getStripePaymentMethods(token, stripeCustomerId);
          if (fallbackResponse?.success) {
            setSavedCards(
              Array.isArray(fallbackResponse?.data)
                ? fallbackResponse.data.filter((x: any) => x && x.id)
                : []
            );
            setSavedBankAccounts([]);
          } else {
            setMethodsLoadError(METHODS_LOAD_FAILED);
          }
        } catch (fallbackError) {
          // Axios interceptor handles Sentry capture
          setMethodsLoadError(METHODS_LOAD_FAILED);
        }
      }
    } finally {
      setCardLoading(false);
      setBankAccountLoading(false);
    }
  };

  // Fetch saved cards when stripeCustomerId changes
  useEffect(() => {
    if (stripeCustomerId && canViewMethods) {
      fetchSavedCards();
    }
  }, [stripeCustomerId, canViewMethods]);

  // Wrap in page container only if standalone (accessed directly, not embedded in another page)
  const isStandalone = !source;
  // The club cannot take cards: from the saved-methods answer (503
  // PAYMENTS_UNAVAILABLE) or the club's own configuration (no publishable key).
  const cardsNotSetUp = paymentsNotSetUp || isCardProcessingNotSetUp();

  const content = (
    <div className="w-full">
      {confirmModal}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className={`text-xl sm:text-2xl font-bold ${getPrimaryText(isDarkMode)} flex-shrink-0`}>
          {t('forms:paymentSettings.title')}
        </h2>
        <AutopaySwitch membershipId={membership?.id} token={token} />
      </div>
      {canSendSetupEmail && (
        <PaymentMethodSetupEmail
          membershipId={membership?.id}
          savedMethodCount={
            cardLoading || bankAccountLoading || methodsLoadError
              ? null
              : (savedCards?.length || 0) + (savedBankAccounts?.length || 0)
          }
        />
      )}
      {/* Stripe-load state banner. Three branches:
          1. !stripe + no error → still initializing (race during first
             few hundred ms after page load).
          2. !stripe + getStripeLoadError() → permanent failure
             (ad-blocker, content-blocker, embed-iframe storage block,
             tenant key timeout). Surfaces actionable next step so
             customers like Jasmine Phillips don't see a silently-
             greyed-out button with no explanation.
          3. stripe ready → no banner.
       */}
      {(() => {
        // Decided from the club's own configuration (no publishable key)
        // as well as from the saved-methods answer: a family with no Stripe
        // customer never makes that request, and was told to refresh the
        // page instead (2026-10-10). Checked before "Stripe is ready": with
        // the card form hidden, this banner is what explains its absence.
        if (cardsNotSetUp) {
          return (
            <div role="alert" className={`mb-4 p-3 rounded border border-amber-300 ${isDarkMode ? 'bg-amber-900/20 text-amber-200' : 'bg-amber-50 text-amber-900'}`}>
              <p className="text-sm font-medium">Card payments are not set up for this club</p>
              <p className="text-xs mt-1">{methodsLoadError || CARDS_NOT_SET_UP}</p>
            </div>
          );
        }
        if (stripe) return null;
        const loadErr =
          getStripeLoadError() ||
          (stripeFailed ? "The secure payment form could not be loaded. Card payments may not be set up for this club; otherwise reload the page." : null);
        if (loadErr) {
          return (
            <div className={`mb-4 p-3 rounded border border-red-300 ${isDarkMode ? 'bg-red-900/20 text-red-200' : 'bg-red-50 text-red-800'}`}>
              <p className="text-sm font-medium">Payment form unavailable</p>
              <p className="text-xs mt-1">{loadErr}</p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="mt-2 text-xs underline"
              >
                Reload page
              </button>
            </div>
          );
        }
        return (
          <div className={`mb-4 p-3 rounded border ${getLightBorder(isDarkMode)} flex items-center gap-2`}>
            <div className="w-4 h-4 border-2 border-gray-400 dark:border-gray-500 border-t-transparent rounded-full animate-spin" />
            <p className={`text-sm ${getSecondaryText(isDarkMode)}`}>Loading secure payment form…</p>
          </div>
        );
      })()}

      {/* Existing card update form. Not drawn when the club cannot take
          cards: the "not set up" banner above said so, and a card-number
          field under it invited a card that could never be saved
          (2026-10-10). */}
      {!canManageMethods && (
        <p data-testid="no-manage-payment-methods" className={`mb-4 text-sm ${getSecondaryText(isDarkMode)}`}>
          {NO_MANAGE_PERMISSION}
        </p>
      )}
      {!cardsNotSetUp && canManageMethods && (
      <form onSubmit={handleSubmit}>
        <div>
          <label className={`block text-sm font-semibold mb-2 ${getSecondaryText(isDarkMode)}`}>
            {t('forms:paymentSettings.creditCardNumber')} *
          </label>
          <div className="flex flex-col sm:flex-row gap-3 mt-2">
            <div className={`flex-1 p-3 sm:p-4 border ${getLightBorder(isDarkMode)} rounded-md ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <CardElement options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: isDarkMode ? '#f3f4f6' : '#374151',
                    '::placeholder': {
                      color: isDarkMode ? '#9ca3af' : '#6b7280',
                    },
                  },
                },
              }} />
            </div>
            <button
              type="submit"
              className="bg-[var(--cc-action)] text-[var(--cc-action-text)] hover:bg-[var(--cc-action-hover)] px-6 py-3 sm:py-3 rounded-md font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 w-full sm:w-auto"
              disabled={!stripe || loading}
            >
              {loading ? t('forms:paymentSettings.addingCard') : t('forms:paymentSettings.addCard')}
            </button>
          </div>
        </div>
      </form>
      )}

      {/* Display saved cards */}
      <div className="mt-6 sm:mt-8">
        <h3 className={`text-lg sm:text-xl font-semibold mb-3 sm:mb-4 ${getPrimaryText(isDarkMode)}`}>{t('forms:paymentSettings.savedCards')}</h3>
        {!canViewMethods ? (
          <p data-testid="no-view-payment-methods" className={getSecondaryText(isDarkMode)}>{NO_VIEW_PERMISSION}</p>
        ) : cardLoading ? (
          <div
            className="w-full flex justify-center items-center py-8"
          >
            <div className="flex justify-center items-center">
              <div className="w-6 h-6 border-2 border-gray-400 dark:border-gray-500 border-t-transparent rounded-full animate-spin"></div>
              <span className={`ml-2 ${getSecondaryText(isDarkMode)}`}>{t('forms:paymentSettings.loadingCards')}</span>
            </div>
          </div>
        ) : methodsLoadError && savedCards?.length === 0 ? (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">{methodsLoadError}</p>
        ) : savedCards?.length === 0 ? (
          <p className={getSecondaryText(isDarkMode)}>{t('forms:paymentSettings.noSavedCards')}</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
            {savedCards?.map((card) => {
              const isDefault = card.id === defaultPaymentMethodId;
              const isDeleting = deletingCardId === card.id;
              const isSettingDefault = settingDefaultId === card.id;
              return (
                <div
                  key={card?.id}
                  className={`${getCardBackground(isDarkMode)} border ${getLightBorder(isDarkMode)} px-4 py-4 rounded-lg ${
                    isDefault ? `ring-2 ${isDarkMode ? 'ring-green-500' : 'ring-green-400'}` : ""
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <p className={`${getPrimaryText(isDarkMode)} text-sm sm:text-base mb-2`}>
                        <strong>{t('forms:paymentSettings.brand')}</strong> {card?.card?.brand?.toUpperCase()}{" "}
                        •••• {card?.card?.last4}
                      </p>
                      <p className={`${getPrimaryText(isDarkMode)} text-sm sm:text-base`}>
                        <strong>{t('forms:paymentSettings.expires')}</strong> {card?.card?.exp_month}/
                        {card?.card?.exp_year}
                        <CardExpiryBadge
                          expMonth={card?.card?.exp_month}
                          expYear={card?.card?.exp_year}
                          className="ml-2 align-middle"
                        />
                      </p>
                      {isDefault ? (
                        <span className="inline-block mt-2 px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                          {t('forms:paymentSettings.default')}
                        </span>
                      ) : canManageMethods && (
                        <button
                          onClick={() => handleSetDefault(card.id)}
                          disabled={isSettingDefault}
                          className={`mt-2 text-xs px-2 py-1 rounded ${
                            isSettingDefault
                              ? "opacity-50 cursor-not-allowed"
                              : "hover:bg-gray-100 dark:hover:bg-gray-700"
                          } ${getSecondaryText(isDarkMode)}`}
                        >
                          {isSettingDefault ? t('forms:paymentSettings.setting') : t('forms:paymentSettings.setAsDefault')}
                        </button>
                      )}
                    </div>
                    {canManageMethods && (
                    <Tooltip content="Delete card"><button aria-label="Delete card"
                      onClick={() => handleDeleteCard(card.id, card?.card?.last4)}
                      disabled={isDeleting}
                      className={`ml-3 p-2 rounded-md transition-colors flex-shrink-0 ${
                        isDeleting
                          ? "opacity-50 cursor-not-allowed bg-gray-200 dark:bg-gray-700"
                          : "hover:bg-red-100 dark:hover:bg-red-900 text-red-600 dark:text-red-400"
                      }`}

                    >
                      {isDeleting ? (
                        <div
                          className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
                          style={{ borderColor: '#ef4444', borderTopColor: 'transparent' }}
                        />
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      )}
                    </button></Tooltip>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Divider */}
      <div className={`my-8 border-t ${getLightBorder(isDarkMode)}`}></div>

      {/* Bank Accounts Section */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <h3 className={`text-lg sm:text-xl font-semibold ${getPrimaryText(isDarkMode)}`}>
            {t('forms:paymentSettings.bankAccounts')}
          </h3>
          {!showAddBankAccount && stripeCustomerId && !cardsNotSetUp && canManageMethods && (
            <button
              onClick={createAchSetupIntent}
              className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 px-4 py-2 rounded-md font-semibold transition-colors flex items-center gap-2 w-full sm:w-auto justify-center"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {t('forms:paymentSettings.addBankAccount')}
            </button>
          )}
        </div>

        {/* Add Bank Account Form */}
        {showAddBankAccount && achClientSecret && (
          <div className={`mb-6 p-4 border ${getLightBorder(isDarkMode)} rounded-lg ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
            <div className="flex justify-between items-center mb-4">
              <h4 className={`font-medium ${getPrimaryText(isDarkMode)}`}>{t('forms:paymentSettings.linkNewBankAccount')}</h4>
              <button
                aria-label="Cancel adding bank account"
                onClick={() => {
                  setShowAddBankAccount(false);
                  setAchClientSecret("");
                }}
                className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${getSecondaryText(isDarkMode)}`}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <BankAccountElement
              clientSecret={achClientSecret}
              stripeCustomerId={stripeCustomerId}
              memberName={`${membership?.first_name || ''} ${membership?.last_name || ''}`}
              onSuccess={handleBankAccountSuccess}
              onCancel={() => {
                setShowAddBankAccount(false);
                setAchClientSecret("");
              }}
            />
          </div>
        )}

        {/* Saved Bank Accounts */}
        {!canViewMethods ? (
          <p className={getSecondaryText(isDarkMode)}>{NO_VIEW_PERMISSION}</p>
        ) : bankAccountLoading ? (
          <div className="w-full flex justify-center items-center py-8">
            <div className="flex justify-center items-center">
              <div className="w-6 h-6 border-2 border-gray-400 dark:border-gray-500 border-t-transparent rounded-full animate-spin"></div>
              <span className={`ml-2 ${getSecondaryText(isDarkMode)}`}>{t('forms:paymentSettings.loadingBankAccounts')}</span>
            </div>
          </div>
        ) : methodsLoadError && savedBankAccounts?.length === 0 ? (
          <p className="text-sm text-red-600 dark:text-red-400">{methodsLoadError}</p>
        ) : savedBankAccounts?.length === 0 ? (
          <p className={getSecondaryText(isDarkMode)}>{t('forms:paymentSettings.noSavedBankAccounts')}</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
            {savedBankAccounts?.map((bankAccount) => {
              const isDefault = bankAccount.id === defaultPaymentMethodId;
              const isDeleting = deletingBankAccountId === bankAccount.id;
              const isSettingDefault = settingDefaultId === bankAccount.id;
              const bankInfo = bankAccount?.us_bank_account;
              return (
                <div
                  key={bankAccount?.id}
                  className={`${getCardBackground(isDarkMode)} border ${getLightBorder(isDarkMode)} px-4 py-4 rounded-lg ${
                    isDefault ? `ring-2 ${isDarkMode ? 'ring-green-500' : 'ring-green-400'}` : ""
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <svg className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <p className={`${getPrimaryText(isDarkMode)} text-sm sm:text-base font-medium`}>
                          {bankInfo?.bank_name || 'Bank Account'}
                        </p>
                      </div>
                      <p className={`${getPrimaryText(isDarkMode)} text-sm sm:text-base mb-1`}>
                        <strong>{bankInfo?.account_type === 'checking' ? t('forms:paymentSettings.checking') : t('forms:paymentSettings.savings')}:</strong> •••• {bankInfo?.last4}
                      </p>
                      {isDefault ? (
                        <span className="inline-block mt-2 px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                          {t('forms:paymentSettings.default')}
                        </span>
                      ) : canManageMethods && (
                        <button
                          onClick={() => handleSetDefault(bankAccount.id)}
                          disabled={isSettingDefault}
                          className={`mt-2 text-xs px-2 py-1 rounded ${
                            isSettingDefault
                              ? "opacity-50 cursor-not-allowed"
                              : "hover:bg-gray-100 dark:hover:bg-gray-700"
                          } ${getSecondaryText(isDarkMode)}`}
                        >
                          {isSettingDefault ? t('forms:paymentSettings.setting') : t('forms:paymentSettings.setAsDefault')}
                        </button>
                      )}
                    </div>
                    {canManageMethods && (
                    <button
                      onClick={() => handleDeleteBankAccount(bankAccount.id, bankInfo?.last4)}
                      disabled={isDeleting}
                      className={`ml-3 p-2 rounded-md transition-colors flex-shrink-0 ${
                        isDeleting
                          ? "opacity-50 cursor-not-allowed bg-gray-200 dark:bg-gray-700"
                          : "hover:bg-red-100 dark:hover:bg-red-900 text-red-600 dark:text-red-400"
                      }`}
                      title={t('common:buttons.delete')}
                    >
                      {isDeleting ? (
                        <div
                          className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
                          style={{ borderColor: '#ef4444', borderTopColor: 'transparent' }}
                        />
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      )}
                    </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  if (isStandalone) {
    return (
      // Only the family's /member/update-card route is framed here. `content`
      // also renders inside MemberBillingHistory on staff pages, so its inner
      // controls are left for the staff sweep (PM 4d6dbcfc).
      <PageContainer data-testid="standalone-wrapper">
        <PageHeading title={t('forms:paymentSettings.updatePaymentMethod')} />
        {content}
      </PageContainer>
    );
  }

  return content;
};

// export default UpdateCardForm;

// Wrapper to load Stripe Elements
const StripeWrapper = (props: any) => {
  // Once per mount: this used to call getStripePromise() on every render,
  // starting a fresh key wait (and its error report) each time.
  const stripePromise = useMemo(() => getStripePromise(), []);
  // When the promise settles with no Stripe, say so; useStripe() alone stays
  // null and the page waited forever.
  const [stripeFailed, setStripeFailed] = useState(false);
  useEffect(() => {
    let live = true;
    Promise.resolve(stripePromise)
      .then((s) => {
        if (live && !s) setStripeFailed(true);
      })
      .catch(() => {
        if (live) setStripeFailed(true);
      });
    return () => {
      live = false;
    };
  }, [stripePromise]);
  return (
    <Elements stripe={stripePromise}>
      <UpdateCardForm {...props} stripeFailed={stripeFailed} />
    </Elements>
  );
};

export default StripeWrapper;
